import {
  AnalyticsActionFavoritesAddEvent,
  AnalyticsActionFavoritesRemoveEvent,
  AnalyticsEventParam,
} from '@accedo/one-plugin-sdk';
import { proxy, useSnapshot } from 'valtio';
import { subscribeKey } from 'valtio/utils';

import { favoritesService, MediaType } from '#/client';
import devtoolsApi from '#/dev/devtools';
import { ignoreError } from '#/service/utils';
import { authStore } from '#/store/auth';

import { addToFavorites, removeFromFavorites } from './favorites.api';

interface FavoritesStore {
  favoriteIds: string[];
}

const defaultState: FavoritesStore = {
  favoriteIds: [],
};

export const favoritesStore = proxy<FavoritesStore>(defaultState);

export const favoritesActions = {
  async load() {
    try {
      const { data } = await favoritesService.getAll();
      const ids = (data.data || []).map((item) => item.objectId);
      favoritesStore.favoriteIds = ids;
    } catch (e) {
      console.warn('Loading user favorites failed', e);
      favoritesActions.reset();
    }
  },

  async addFavorite(itemId: string, mediaType: MediaType) {
    if (favoritesStore.favoriteIds.includes(itemId)) {
      return;
    }

    try {
      // call api
      const mediaResource = await addToFavorites(itemId, mediaType);

      // #ExternalAnalytics - Add to favorites
      document.dispatchEvent(
        new AnalyticsActionFavoritesAddEvent({
          [AnalyticsEventParam.ContentId]: itemId,
          [AnalyticsEventParam.Title]: mediaResource.title,
        })
      );

      favoritesStore.favoriteIds.push(itemId);
    } catch (err) {
      console.warn(err);
    }
  },

  async removeFavorite(itemId: string, mediaType: MediaType) {
    if (!favoritesStore.favoriteIds.includes(itemId)) {
      return;
    }

    try {
      // call api
      const mediaResource = await removeFromFavorites(itemId, mediaType);

      // #ExternalAnalytics - Remove from favorites
      document.dispatchEvent(
        new AnalyticsActionFavoritesRemoveEvent({
          [AnalyticsEventParam.ContentId]: itemId,
          [AnalyticsEventParam.Title]: mediaResource.title,
        })
      );

      favoritesStore.favoriteIds = favoritesStore.favoriteIds.filter(
        (id) => id !== itemId
      );
    } catch (err) {
      console.warn(err);
    }
  },

  reset() {
    favoritesStore.favoriteIds = [];
  },
};

// watch for changes in the logged in state, and load/reset favorites accordingly.
subscribeKey(authStore, 'isLoggedIn', (isLoggedIn) => {
  if (isLoggedIn) {
    favoritesActions.load().catch(ignoreError);
  } else {
    favoritesActions.reset();
  }
});

export const useFavorites = () => useSnapshot(favoritesStore) as FavoritesStore;

devtoolsApi.setStore('favorites', favoritesStore, favoritesActions);
